import { Business, Job } from "@prisma/client";
import { GetServerSideProps } from "next";
import { NextSeo } from "next-seo";
import dynamic from "next/dynamic";
import { useMemo } from "react";

import { Container } from "@/features/common/components/Container";
import { PaginationProvider } from "@/features/common/contexts/usePaginationContext";
import { useSeoProps } from "@/features/common/hooks/useSeoProps";
import { useJobListWhereQuery } from "@/features/job/hooks/useJobListWhereQuery";
import { SiteLayout } from "@/features/layout/SiteLayout";
import {
  JobStatusEnum,
  SortOrder,
  useJobsCountQuery,
} from "@/generated/graphql-hooks";
import { getPublicConfig } from "@/helpers/getPublicConfig";
import { getImageUrl } from "@/services/cloudinary/getImageUrl";
import { prisma } from "@/services/prisma";

const JobList = dynamic(
  () => import("@/features/job/components/jobList/JobList"),
);

const defaultWhere = {
  status: {
    not: {
      equals: JobStatusEnum.Cancelled,
    },
  },
};

const { features } = getPublicConfig();

type JobWithCreator = Job & {
  creator: Pick<Business, "logoPublicId">;
};

type PageProps = {
  job?: Pick<JobWithCreator, "title" | "description" | "creator">;
};

const JobListPage = ({ job }: PageProps) => {
  const where = useJobListWhereQuery();

  const jobListWhere = useMemo(
    () => ({
      ...where,
      ...defaultWhere,
    }),
    [where],
  );

  const creatorLogoUrl = job?.creator?.logoPublicId
    ? getImageUrl(job.creator.logoPublicId, {
        size: features.common.seoOgImageSize,
      })
    : null;

  const seoProps = useSeoProps({
    title: job?.title,
    description: job?.description,
    openGraph: {
      images: creatorLogoUrl
        ? [
            {
              url: creatorLogoUrl,
              width: features.common.seoOgImageSize,
              height: features.common.seoOgImageSize,
              alt: job?.title,
              type: "image/jpeg",
            },
          ]
        : undefined,
    },
  });

  const { data: { jobsCount } = {} } = useJobsCountQuery({
    where: jobListWhere,
  });

  return (
    <PaginationProvider>
      <NextSeo {...seoProps} />
      <Container className="pt-1 pb-12 sm:grid sm:grid-cols-4 sm:gap-x-8 sm:pt-12 sm:pb-24">
        <JobList
          count={jobsCount}
          where={jobListWhere}
          orderBy={[{ status: SortOrder.Desc }, { createdAt: SortOrder.Desc }]}
        />
      </Container>
    </PaginationProvider>
  );
};

export default JobListPage;

JobListPage.getLayout = function getLayout(page) {
  return <SiteLayout>{page}</SiteLayout>;
};

export const getServerSideProps: GetServerSideProps<PageProps> = async (
  ctx,
) => {
  const { query } = ctx;
  const jobId = query.jobId as string;
  if (!jobId) {
    return { props: {} };
  }

  const job = await prisma.job.findUnique({
    where: { id: jobId },
    include: {
      creator: { select: { logoPublicId: true } },
    },
  });

  const { title, description, creator } = job;

  return {
    props: {
      job: { title, description, creator },
    },
  };
};
